import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@material-ui/core'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Wrap } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { ButtonVerticalGroup } from 'shared/components/Button/ButtonVerticalGroup'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { LocationDropdown } from 'shared/components/Dropdown/LocationDropdown'
import { MonthYearInput } from 'shared/components/MaskedInput/MonthYearInput'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { Handler } from 'shared/helpers/typeHelper'
import { convertBooleanToYesNo, YesNoRadioOptions } from 'shared/helpers/utils'
import { YesNo } from 'shared/models'
import {
  createDrivingAccident,
  deleteDrivingAccident,
  DrivingAccident,
  DrivingViolationType,
  getAccidentTypeDetailValues,
  getDrivingViolationTypeDetailValues,
  updateDrivingAccident,
} from 'shared/models/employment'
import { updateUserInfo } from 'shared/models/user'
import { sizes } from 'shared/theme'
import { mixed } from 'yup'

import { User } from '../../../models/user'
import { useUserProvider } from '../../Context/UserContext'
import { createViolationPayload, ViolationForm, ViolationSchema } from './AddViolation'
import { ConfirmSingleDelete } from './ConfirmSingleDelete'

interface AddAccidentProps {
  accident?: DrivingAccident | {} | null
  onSuccess: Handler
  onClose?: Handler
  user: User
}

interface AccidentForm extends ViolationForm {
  accidentType: string
  isDotRecordable: YesNo | null
  isCharged: YesNo | null
}

const AccidentSchema = ViolationSchema.shape({
  violationType: mixed().test(function (val) {
    if (!val && this.parent.isCharged === YesNo.YES) {
      return this.createError({ message: 'This field is required.', path: 'violationType' })
    }
    return true
  }),
  violationTypeDetail: mixed().test(function (val) {
    if (
      !val &&
      this.parent.violationType === DrivingViolationType.OTHER &&
      this.parent.isCharged === YesNo.YES
    ) {
      return this.createError({ message: 'This field is required.', path: 'violationTypeDetail' })
    }
    return true
  }),
  accidentType: mixed().required('Please make a selection.'),
  isDotRecordable: mixed().required('Please make a selection.'),
  isCharged: mixed().required('Please make a selection.'),
})

function getInitialForm(accident?: DrivingAccident | {} | null) {
  if (!accident || !('address' in accident)) {
    return {
      address: '',
      date: '',
      isInCommercialVehicle: undefined,
      isDotRecordable: undefined,
      isCharged: undefined,
      violationType: '',
      violationTypeDetail: '',
    }
  }
  return {
    accidentType: accident?.accidentType || '',
    address: accident?.address || '',
    date: accident?.dateFormatted || '',
    isInCommercialVehicle: convertBooleanToYesNo(accident?.isInCommercialVehicle),
    isDotRecordable: convertBooleanToYesNo(accident?.isDotRecordable),
    isCharged: convertBooleanToYesNo(accident?.isCharged),
    violationType: accident?.violationType,
    violationTypeDetail: accident?.violationTypeDetail,
  }
}

function createAccidentPayload(formData: AccidentForm) {
  return {
    ...createViolationPayload(formData),
    accident_type: formData.accidentType,
    is_dot_recordable: formData.isDotRecordable === YesNo.YES,
    is_charged: formData.isCharged === YesNo.YES,
  }
}

export function AddAccident({ accident, onSuccess, onClose, user }: AddAccidentProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const initialForm = getInitialForm(accident)
  const isEditing = accident && 'id' in accident
  const { showBanner, clearUserCache } = useUserProvider()

  const { control, handleSubmit, watch } = useForm<AccidentForm>({
    mode: 'onSubmit',
    defaultValues: initialForm,
    resolver: yupResolver(AccidentSchema),
  })

  const violationType = watch('violationType')
  const isCharged = watch('isCharged')

  async function onSubmit(formData: AccidentForm) {
    setIsLoading(true)
    try {
      const payload = createAccidentPayload(formData)
      // This is required on BE, but not in UI
      if (!payload.violation_type) payload.violation_type = DrivingViolationType.OTHER
      if (isEditing) {
        await updateDrivingAccident(user.id, accident.id, payload)
      } else {
        await createDrivingAccident(user.id, payload)
      }

      if (!user.driver?.hadAtFaultIncident) {
        await updateUserInfo(user.id, { driver: { had_at_fault_incident: true } }, { v3Api: true })
        await clearUserCache()
      }

      onSuccess()
      setIsLoading(false)
    } catch (err: any) {
      showBanner('error', err)
      setIsLoading(false)
    }
  }

  async function handleRemove() {
    if (!isEditing) return null
    try {
      setIsLoading(true)
      await deleteDrivingAccident(user.id, accident.id)
      setIsLoading(false)
      onSuccess()
    } catch (err: any) {
      showBanner('error', err)
      setIsLoading(false)
    }
  }

  return (
    <Wrap width="100%">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text variant="header2" align="center">
          {isEditing ? 'Edit' : 'Add'} an accident
        </Text>
        <Box m={6} />

        <Controller
          render={({ field, fieldState }) => (
            <RadioButtonGroup
              {...field}
              row
              options={getAccidentTypeDetailValues()}
              error={fieldState.error?.message}
              width="100%"
            />
          )}
          name="accidentType"
          control={control}
        />

        <Controller
          render={({ field, fieldState }) => (
            <LocationDropdown
              {...field}
              inputProps={{
                label: 'City, state',
              }}
              error={fieldState.error?.message}
            />
          )}
          name="address"
          control={control}
        />
        <Box mt={2} />

        <Controller
          render={({ field, fieldState }) => (
            <MonthYearInput {...field} label="Date" error={fieldState.error?.message} />
          )}
          name="date"
          control={control}
        />

        <Box mt={5} />

        <Controller
          render={({ field, fieldState }) => (
            <RadioButtonGroup
              {...field}
              row
              label={'Were you in a commercial vehichle?'}
              options={YesNoRadioOptions}
              error={fieldState.error?.message}
              width="100%"
              noMargin
              inLine
            />
          )}
          name="isInCommercialVehicle"
          control={control}
        />

        <Box mt={2} />
        <Controller
          render={({ field, fieldState }) => (
            <RadioButtonGroup
              {...field}
              row
              label={'Was this a DOT recordable accident?'}
              options={YesNoRadioOptions}
              error={fieldState.error?.message}
              width="100%"
              noMargin
              inLine
            />
          )}
          name="isDotRecordable"
          control={control}
        />

        <Box mt={2} />
        <Controller
          render={({ field, fieldState }) => (
            <RadioButtonGroup
              {...field}
              row
              label={'Were you charged with a violation?'}
              options={YesNoRadioOptions}
              error={fieldState.error?.message}
              width="100%"
              inLine
            />
          )}
          name="isCharged"
          control={control}
        />

        {isCharged === YesNo.YES && (
          <>
            <Controller
              render={({ field, fieldState }) => (
                <Dropdown
                  {...field}
                  isFullWidth
                  error={fieldState.error?.message}
                  options={getDrivingViolationTypeDetailValues()}
                  label="Violation Type"
                  staticLabel="Violation Type"
                  maxHeight={550}
                />
              )}
              name="violationType"
              control={control}
            />
            {violationType === DrivingViolationType.OTHER && (
              <>
                <Controller
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      placeholder="Add a short explanation"
                      error={fieldState.error?.message}
                    />
                  )}
                  name="violationTypeDetail"
                  control={control}
                />
              </>
            )}
          </>
        )}

        <Box mt={8} />
        <Button type="submit" size={sizes.extraLarge} margin="0 auto" isLoading={isLoading}>
          Continue
        </Button>
        <Box mt={4} />

        <ButtonVerticalGroup
          leftButton={
            <Button variant="text" onClick={onClose} margin="0 auto">
              Cancel
            </Button>
          }
          rightButton={
            isEditing ? (
              <Button variant="text" onClick={() => setIsDeleting(true)} margin="0 auto">
                Remove
              </Button>
            ) : null
          }
        />
        {isDeleting && (
          <ConfirmSingleDelete
            onSuccess={handleRemove}
            onClose={() => setIsDeleting(false)}
            isLoading={isLoading}
            headlineLabel="Remove accident?"
            confirmLabel="Remove accident"
          />
        )}
      </form>
    </Wrap>
  )
}
